// SCR-STAFF-982 — تنظیمات ویزیت آنلاین (US-TEL-001؛ AP-TEL-01): پنجره باز شدن اتاق انتظار، هشدار تأخیر و غیبت ارائه‌دهنده، مهلت اتصال
// مجدد، کمینه مدت جلسه سالم، الزام رضایت‌نامه از راه دور و قالب آن، و فهرست داروهای ممنوع ویزیت از راه دور (BR-TEL-020؛ CFG-042).
// خدمت «مجازی» خودش در کتاب خدمت CAT علامت می‌خورد (`isVirtual`)، پس این صفحه فقط سیاست‌های جلسه را نگه می‌دارد.
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type TelSettingsView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, FormPage } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SCR_STAFF_982, telKeys, telNum } from './common';

interface SettingsForm {
  waitingRoomOpensMinutesBefore: string;
  providerLateWarningMinutes: string;
  providerNoShowMinutes: string;
  reconnectGraceMinutes: string;
  minimumHealthySessionMinutes: string;
  consentRequired: boolean;
  consentTemplateCode: string;
  restrictedDrugCodes: string;
}

function toForm(data: TelSettingsView): SettingsForm {
  return {
    waitingRoomOpensMinutesBefore: String(telNum(data.waitingRoomOpensMinutesBefore)),
    providerLateWarningMinutes: String(telNum(data.providerLateWarningMinutes)),
    providerNoShowMinutes: String(telNum(data.providerNoShowMinutes)),
    reconnectGraceMinutes: String(telNum(data.reconnectGraceMinutes)),
    minimumHealthySessionMinutes: String(telNum(data.minimumHealthySessionMinutes)),
    consentRequired: data.consentRequired,
    consentTemplateCode: data.consentTemplateCode,
    restrictedDrugCodes: data.restrictedDrugCodes.join('\n'),
  };
}

function drugList(value: string): string[] {
  return value
    .split('\n')
    .map((code) => code.trim())
    .filter((code) => code !== '');
}

export function TelehealthSettingsPage() {
  const { t } = useTranslation('tel');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const readOnly = !(access.accessMode === 'Full' && access.can('tel.settings.manage'));
  const settings = useQuery({
    queryKey: telKeys.settings(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/telehealth/settings')),
  });
  // نسخه ذخیره‌شده مرجع Dirty است؛ تا نخستین ویرایش هیچ محافظ خروجی فعال نمی‌شود.
  const [draft, setDraft] = useState<SettingsForm | null>(null);
  const saved = settings.data ? toForm(settings.data) : null;
  const form = draft ?? saved;
  const save = useMutation({
    mutationFn: (values: SettingsForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/telehealth/settings', {
          body: {
            waitingRoomOpensMinutesBefore: Number(values.waitingRoomOpensMinutesBefore),
            providerLateWarningMinutes: Number(values.providerLateWarningMinutes),
            providerNoShowMinutes: Number(values.providerNoShowMinutes),
            reconnectGraceMinutes: Number(values.reconnectGraceMinutes),
            minimumHealthySessionMinutes: Number(values.minimumHealthySessionMinutes),
            consentRequired: values.consentRequired,
            consentTemplateCode: values.consentTemplateCode.trim(),
            restrictedDrugCodes: drugList(values.restrictedDrugCodes),
          },
        }),
      ),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: telKeys.settings(session?.tenantId) });
    },
  });
  const set = (patch: Partial<SettingsForm>) =>
    setDraft((current) => {
      const base = current ?? saved;
      return base ? { ...base, ...patch } : null;
    });
  return (
    <FormPage
      screenId={SCR_STAFF_982}
      titleKey="tel:settings.title"
      subtitleKey="tel:settings.subtitle"
      isLoading={settings.isPending}
      loadError={settings.error}
      readOnly={readOnly}
      isDirty={draft !== null && !save.isPending}
      isSubmitting={save.isPending}
      submitError={save.error}
      onSubmit={() => form && save.mutate(form)}
      notice={
        readOnly ? (
          <Alert severity="info" data-testid="tel-settings-readonly">
            {t('tel:settings.readonly')}
          </Alert>
        ) : save.isSuccess && draft === null ? (
          <Alert severity="success" data-testid="tel-settings-saved">
            {t('tel:settings.saved')}
          </Alert>
        ) : undefined
      }
    >
      {form ? (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('tel:settings.window_title')}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('tel:settings.waiting_room_opens')}
                value={form.waitingRoomOpensMinutesBefore}
                disabled={readOnly}
                onChange={(event) => set({ waitingRoomOpensMinutesBefore: event.target.value })}
                slotProps={{
                  htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'tel-settings-waiting' },
                }}
              />
              <TextField
                label={t('tel:settings.provider_late')}
                value={form.providerLateWarningMinutes}
                disabled={readOnly}
                onChange={(event) => set({ providerLateWarningMinutes: event.target.value })}
                slotProps={{
                  htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'tel-settings-late' },
                }}
              />
              <TextField
                label={t('tel:settings.provider_no_show')}
                value={form.providerNoShowMinutes}
                disabled={readOnly}
                onChange={(event) => set({ providerNoShowMinutes: event.target.value })}
                slotProps={{
                  htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'tel-settings-no-show' },
                }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('tel:settings.no_show_hint')}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('tel:settings.session_title')}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('tel:settings.reconnect_grace')}
                value={form.reconnectGraceMinutes}
                disabled={readOnly}
                onChange={(event) => set({ reconnectGraceMinutes: event.target.value })}
                slotProps={{
                  htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'tel-settings-reconnect' },
                }}
              />
              <TextField
                label={t('tel:settings.minimum_healthy')}
                value={form.minimumHealthySessionMinutes}
                disabled={readOnly}
                onChange={(event) => set({ minimumHealthySessionMinutes: event.target.value })}
                slotProps={{
                  htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'tel-settings-minimum' },
                }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('tel:settings.minimum_hint')}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('tel:settings.consent_title')}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.consentRequired}
                    disabled={readOnly}
                    onChange={(event) => set({ consentRequired: event.target.checked })}
                    slotProps={{ input: { 'aria-label': t('tel:settings.consent_required') } }}
                  />
                }
                label={t('tel:settings.consent_required')}
              />
              <TextField
                label={t('tel:settings.consent_template')}
                value={form.consentTemplateCode}
                disabled={readOnly}
                onChange={(event) => set({ consentTemplateCode: event.target.value })}
                slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tel-settings-consent-template' } }}
              />
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('tel:settings.drugs_title')}
              <Chip
                size="small"
                sx={{ marginInlineStart: 1 }}
                label={faDigits(drugList(form.restrictedDrugCodes).length)}
                data-testid="tel-settings-drug-count"
              />
            </Typography>
            <TextField
              label={t('tel:settings.drugs')}
              value={form.restrictedDrugCodes}
              multiline
              minRows={4}
              fullWidth
              disabled={readOnly}
              onChange={(event) => set({ restrictedDrugCodes: event.target.value })}
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tel-settings-drugs' } }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('tel:settings.drugs_hint')}
            </Typography>
          </Paper>
        </>
      ) : null}
    </FormPage>
  );
}
